import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";

const MAX_SOURCE_FILE_BYTES = 1024 * 1024; // 1 MB source file cap
const TARGET_SIGNATURE_BYTES = 60 * 1024;
const HARD_SIGNATURE_BYTES = 80 * 1024;
const SIGNATURE_MAX_WIDTH = 320;
const SIGNATURE_MAX_HEIGHT = 120;
const QUALITY_STEPS = [0.65, 0.55, 0.45, 0.35];
const SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6];

const getDecodedBytes = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const base64 = dataUrl.includes("base64,")
    ? dataUrl.split("base64,")[1]
    : dataUrl;
  if (!base64) return 0;
  return Math.floor((base64.length * 3) / 4);
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = window.URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      window.URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      window.URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read signature image"));
    };
    img.src = objectUrl;
  });

const getBoundedDimensions = (width, height, scale = 1) => {
  const safeWidth =
    Number.isFinite(width) && width > 0 ? width : SIGNATURE_MAX_WIDTH;
  const safeHeight =
    Number.isFinite(height) && height > 0 ? height : SIGNATURE_MAX_HEIGHT;

  const boundedScale = Math.min(
    SIGNATURE_MAX_WIDTH / safeWidth,
    SIGNATURE_MAX_HEIGHT / safeHeight,
    1,
  );

  const finalScale = Math.max(0.1, boundedScale * scale);

  return {
    width: Math.max(1, Math.round(safeWidth * finalScale)),
    height: Math.max(1, Math.round(safeHeight * finalScale)),
  };
};

const buildCompressedDataUrl = (img, width, height, quality) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to process signature image");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(img, 0, 0, width, height);

  return canvas.toDataURL("image/jpeg", quality);
};

const compressSignatureImage = async (file) => {
  const img = await loadImage(file);
  const candidates = [];

  for (const scale of SCALE_STEPS) {
    const { width, height } = getBoundedDimensions(
      img.width,
      img.height,
      scale,
    );

    for (const quality of QUALITY_STEPS) {
      const dataUrl = buildCompressedDataUrl(img, width, height, quality);
      const bytes = getDecodedBytes(dataUrl);
      candidates.push({ dataUrl, bytes, width, height, quality });

      if (bytes <= TARGET_SIGNATURE_BYTES) {
        return { dataUrl, bytes };
      }
    }
  }

  if (candidates.length === 0) {
    throw new Error("Unable to process signature image");
  }

  candidates.sort((left, right) => left.bytes - right.bytes);
  return { dataUrl: candidates[0].dataUrl, bytes: candidates[0].bytes };
};

const Signature = ({ answer, onChange, isDisabled }) => {
  const value =
    answer?.value && typeof answer.value === "object" ? answer.value : {};
  const signatureSrc = value?.signature || null;
  const [name, setName] = useState(value?.name || "");
  const [position, setPosition] = useState(value?.position || "");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setName(value?.name || "");
    setPosition(value?.position || "");
  }, [value?.name, value?.position]);

  const handleOpenPicker = () => {
    if (isDisabled || signatureSrc) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type || !file.type.startsWith("image/")) {
      setError("Please upload an image file.");
      return;
    }

    if (file.size > MAX_SOURCE_FILE_BYTES) {
      setError("Image is too large. Maximum upload size is 1 MB.");
      return;
    }

    const trimmedName = String(name || "").trim();
    const trimmedPosition = String(position || "").trim();

    try {
      const compressed = await compressSignatureImage(file);

      if (compressed.bytes > HARD_SIGNATURE_BYTES) {
        setError(
          "Signature image is too large after compression. Please use a simpler image.",
        );
        return;
      }

      setError("");
      onChange?.({
        name: trimmedName,
        position: trimmedPosition,
        signature: compressed.dataUrl,
      });
    } catch (err) {
      console.error("Signature upload error:", err);
      setError("Unable to process signature image.");
    }
  };

  const persistMetadata = (nextName, nextPosition) => {
    if (!signatureSrc || isDisabled) return;

    const trimmedName = String(nextName || "").trim();
    const trimmedPosition = String(nextPosition || "").trim();
    const currentName = String(value?.name || "").trim();
    const currentPosition = String(value?.position || "").trim();

    if (trimmedName === currentName && trimmedPosition === currentPosition) {
      return;
    }

    onChange?.({
      signature: signatureSrc,
      name: trimmedName,
      position: trimmedPosition,
    });
  };

  if (!signatureSrc) {
    if (isDisabled) {
      return (
        <Box
          sx={{
            width: "100%",
            color: "text.secondary",
            fontStyle: "italic",
            fontSize: 14,
          }}
        >
          No signature available.
        </Box>
      );
    }

    return (
      <Stack spacing={1}>
        <TextField
          size="small"
          label="Name (Optional)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={isDisabled}
          fullWidth
        />
        <TextField
          size="small"
          label="Position (Optional)"
          value={position}
          onChange={(event) => setPosition(event.target.value)}
          disabled={isDisabled}
          fullWidth
        />

        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpenPicker}
            disabled={isDisabled}
          >
            Upload Signature Image
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>

        <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
          Images are compressed for fast save and export quality.
        </Typography>

        {error ? (
          <Typography sx={{ color: "error.main", fontSize: 12 }}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <Box sx={{ width: "100%" }}>
        <img
          src={signatureSrc}
          width="100%"
          alt="Signature"
        />
      </Box>

      <TextField
        size="small"
        label="Name (Optional)"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => persistMetadata(name, position)}
        disabled={isDisabled}
        fullWidth
      />
      <TextField
        size="small"
        label="Position (Optional)"
        value={position}
        onChange={(event) => setPosition(event.target.value)}
        onBlur={() => persistMetadata(name, position)}
        disabled={isDisabled}
        fullWidth
      />

      <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
        Name and Position can be added or updated after upload.
      </Typography>
    </Stack>
  );
};

export default Signature;
